/**
 * Folder Service
 * Verwaltet Desktop-Ordner über User Settings
 * Nutzt bestehende Settings-API, keine neuen Backend-Endpoints
 */

import { SettingsService } from '@/services/settings-service'
import { logError, logInfo } from '@/lib/logger'
import type { IconPosition, SettingsState } from '@/types/settings'

export interface DesktopFolder {
  id: string                    // UUID für Ordner
  name: string                  // Ordnername (z.B. "Produktivität")
  appIds: string[]              // Array der enthaltenen App-IDs
  position: IconPosition        // Grid-Position {row, col}
  color?: string                // Optionale Ordnerfarbe
  createdAt: number             // Erstellungszeitpunkt
}

/**
 * Generiert UUID (einfache Implementierung)
 */
function generateId(): string {
  return `folder-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
}

/**
 * Schreibt nur die Ordner-Keys. Ein Full-Save über die gerade geladenen
 * Settings würde bei jedem Ordner-Klick auch Theme, Apps und Fenster
 * mitschreiben (#908).
 */
async function patchFolderSettings(changes: Partial<Pick<SettingsState, 'desktopFolders' | 'desktopIconPositions'>>): Promise<void> {
  const success = await SettingsService.patchSettings(changes)
  if (!success) {
    throw new Error('Ordner konnten nicht gespeichert werden')
  }
}

/**
 * Desktop Folder Service
 * Alle Operationen erfolgen über Settings (JSONB in PostgreSQL)
 */
export const folderService = {
  /**
   * Erstellt einen neuen Ordner mit den angegebenen Apps
   */
  async createFolder(
    appIds: string[],
    position: IconPosition,
    name?: string
  ): Promise<string> {
    try {
      if (appIds.length < 2) {
        throw new Error('Ein Ordner benötigt mindestens 2 Apps')
      }

      const settings = await SettingsService.loadUserSettings()
      const folders = settings.desktopFolders || {}
      const positions = settings.desktopIconPositions || {}

      // Neuen Ordner erstellen
      const folderId = generateId()
      const folder: DesktopFolder = {
        id: folderId,
        name: name || 'Ordner',
        appIds: [...appIds],
        position,
        createdAt: Date.now(),
      }

      // Apps aus desktopIconPositions entfernen (sie sind jetzt im Ordner)
      const updatedPositions = { ...positions }
      appIds.forEach((appId) => {
        delete updatedPositions[appId]
      })

      // Ordner-Position hinzufügen
      updatedPositions[folderId] = position

      await patchFolderSettings({
        desktopFolders: {
          ...folders,
          [folderId]: folder,
        },
        desktopIconPositions: updatedPositions,
      })

      // Event für UI-Update
      window.dispatchEvent(new CustomEvent('settings-updated'))

      logInfo(`Ordner "${folder.name}" mit ${appIds.length} Apps erstellt`)
      return folderId
    } catch (error) {
      logError(error as Error, { context: 'folderService.createFolder' })
      throw error
    }
  },

  /**
   * Fügt eine App zu einem bestehenden Ordner hinzu
   */
  async addAppToFolder(folderId: string, appId: string): Promise<void> {
    try {
      const settings = await SettingsService.loadUserSettings()
      const folders = settings.desktopFolders || {}
      const folder = folders[folderId]

      if (!folder) {
        throw new Error(`Ordner ${folderId} nicht gefunden`)
      }

      // Prüfe ob App bereits im Ordner
      if (folder.appIds.includes(appId)) {
        logInfo(`App ${appId} ist bereits in Ordner ${folderId}`)
        return
      }

      // App hinzufügen
      const updatedFolder = {
        ...folder,
        appIds: [...folder.appIds, appId],
      }

      // App aus desktopIconPositions entfernen
      const positions = { ...settings.desktopIconPositions }
      delete positions[appId]

      await patchFolderSettings({
        desktopFolders: {
          ...folders,
          [folderId]: updatedFolder,
        },
        desktopIconPositions: positions,
      })

      // Event für UI-Update
      window.dispatchEvent(new CustomEvent('settings-updated'))

      logInfo(`App ${appId} zu Ordner "${folder.name}" hinzugefügt`)
    } catch (error) {
      logError(error as Error, { context: 'folderService.addAppToFolder', folderId, appId })
      throw error
    }
  },

  /**
   * Entfernt eine App aus einem Ordner
   * Optional: Position für die App auf dem Desktop angeben
   */
  async removeAppFromFolder(
    folderId: string,
    appId: string,
    newPosition?: IconPosition
  ): Promise<void> {
    try {
      const settings = await SettingsService.loadUserSettings()
      const folders = settings.desktopFolders || {}
      const folder = folders[folderId]

      if (!folder) {
        throw new Error(`Ordner ${folderId} nicht gefunden`)
      }

      // App aus Ordner entfernen
      const updatedAppIds = folder.appIds.filter((id) => id !== appId)

      // Wenn Ordner danach leer ist, lösche ihn komplett
      if (updatedAppIds.length === 0) {
        await this.deleteFolder(folderId)
        return
      }

      const updatedFolder = {
        ...folder,
        appIds: updatedAppIds,
      }

      // App zurück auf Desktop (mit Position)
      const positions = { ...settings.desktopIconPositions }
      if (newPosition) {
        positions[appId] = newPosition
      }

      await patchFolderSettings({
        desktopFolders: {
          ...folders,
          [folderId]: updatedFolder,
        },
        desktopIconPositions: positions,
      })

      // Event für UI-Update
      window.dispatchEvent(new CustomEvent('settings-updated'))

      logInfo(`App ${appId} aus Ordner "${folder.name}" entfernt`)
    } catch (error) {
      logError(error as Error, { context: 'folderService.removeAppFromFolder', folderId, appId })
      throw error
    }
  },

  /**
   * Benennt einen Ordner um
   */
  async renameFolder(folderId: string, newName: string): Promise<void> {
    try {
      if (!newName || newName.trim().length === 0) {
        throw new Error('Ordnername darf nicht leer sein')
      }

      const settings = await SettingsService.loadUserSettings()
      const folders = settings.desktopFolders || {}
      const folder = folders[folderId]

      if (!folder) {
        throw new Error(`Ordner ${folderId} nicht gefunden`)
      }

      const updatedFolder = {
        ...folder,
        name: newName.trim(),
      }

      await patchFolderSettings({
        desktopFolders: {
          ...folders,
          [folderId]: updatedFolder,
        },
      })

      // Event für UI-Update
      window.dispatchEvent(new CustomEvent('settings-updated'))

      logInfo(`Ordner umbenannt: "${folder.name}" → "${newName}"`)
    } catch (error) {
      logError(error as Error, { context: 'folderService.renameFolder', folderId, newName })
      throw error
    }
  },

  /**
   * Löscht einen Ordner und packt alle Apps zurück auf den Desktop
   */
  async deleteFolder(folderId: string): Promise<void> {
    try {
      const settings = await SettingsService.loadUserSettings()
      const folders = settings.desktopFolders || {}
      const folder = folders[folderId]

      if (!folder) {
        logInfo(`Ordner ${folderId} existiert nicht mehr`)
        return
      }

      // Alle Apps aus dem Ordner zurück auf Desktop
      const positions = { ...settings.desktopIconPositions }

      // Finde freie Positionen für jede App
      const occupiedPositions = new Set<string>()
      Object.values(positions).forEach((pos) => {
        occupiedPositions.add(`${pos.row}:${pos.col}`)
      })

      folder.appIds.forEach((appId, index) => {
        // Finde nächste freie Position
        let row = folder.position.row
        let col = folder.position.col + index + 1 // Rechts neben Ordner platzieren

        // Suche freie Position
        while (occupiedPositions.has(`${row}:${col}`)) {
          col++
          if (col > 9) {
            col = 0
            row++
          }
          if (row > 9) {
            row = 0
            col = 0
          }
        }

        positions[appId] = { row, col }
        occupiedPositions.add(`${row}:${col}`)
      })

      // Ordner entfernen
      const updatedFolders = { ...folders }
      delete updatedFolders[folderId]

      // Ordner-Position aus Icon-Positionen entfernen
      delete positions[folderId]

      await patchFolderSettings({
        desktopFolders: updatedFolders,
        desktopIconPositions: positions,
      })

      // Event für UI-Update
      window.dispatchEvent(new CustomEvent('settings-updated'))

      logInfo(`Ordner "${folder.name}" gelöscht, ${folder.appIds.length} Apps wiederhergestellt`)
    } catch (error) {
      logError(error as Error, { context: 'folderService.deleteFolder', folderId })
      throw error
    }
  },

  /**
   * Aktualisiert die Position eines Ordners auf dem Desktop
   */
  async updateFolderPosition(folderId: string, position: IconPosition): Promise<void> {
    try {
      const settings = await SettingsService.loadUserSettings()
      const folders = settings.desktopFolders || {}
      const folder = folders[folderId]

      if (!folder) {
        throw new Error(`Ordner ${folderId} nicht gefunden`)
      }

      const updatedFolder = {
        ...folder,
        position,
      }

      // Auch in desktopIconPositions aktualisieren
      const positions = { ...settings.desktopIconPositions }
      positions[folderId] = position

      await patchFolderSettings({
        desktopFolders: {
          ...folders,
          [folderId]: updatedFolder,
        },
        desktopIconPositions: positions,
      })

      logInfo(`Ordner-Position aktualisiert: ${folderId}`)
    } catch (error) {
      logError(error as Error, { context: 'folderService.updateFolderPosition', folderId })
      throw error
    }
  },

  /**
   * Lädt alle Ordner aus den Settings
   */
  async getFolders(): Promise<Record<string, DesktopFolder>> {
    try {
      const settings = await SettingsService.loadUserSettings()
      return settings.desktopFolders || {}
    } catch (error) {
      logError(error as Error, { context: 'folderService.getFolders' })
      return {}
    }
  },

  /**
   * Prüft ob ein Ordner existiert
   */
  async folderExists(folderId: string): Promise<boolean> {
    try {
      const folders = await this.getFolders()
      return folderId in folders
    } catch (error) {
      logError(error as Error, { context: 'folderService.folderExists', folderId })
      return false
    }
  },

  /**
   * Holt einen spezifischen Ordner
   */
  async getFolder(folderId: string): Promise<DesktopFolder | null> {
    try {
      const folders = await this.getFolders()
      return folders[folderId] || null
    } catch (error) {
      logError(error as Error, { context: 'folderService.getFolder', folderId })
      return null
    }
  },
}
